// State.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  CircularProgress,
  Snackbar,
} from "@material-ui/core";
import axios from "axios";
import StateTable from "./StateTable";
import { BACKEND_BASE_URL } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Pagination } from "@mui/material";
import { TextField } from "@material-ui/core";
import FileUploadDialog from "./FileUploadDialog";
import EntryFormDialog from "./EntryFormDialog";

export default function State() {
  const [data, setData] = useState([]);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isFileDialogOpen, setIsFileDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    country__name: "",
    country: "",
    state: "",
    county: "",
    municipality_name: "",
    population: "",
    latitude: "",
    longitude: "",
    unlock_city: false,
    explain_requests_per_user: 0,
    ask_aware_requests_per_user: 0,
    agreement_start_date: "",
    agreement_end_date: "",
    reminder_days: 90,
    notes: "",
    attachment: null,  // file
    is_locked_from_partnerCity: false, // new field
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [liveCitiesCount, setLiveCitiesCount] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${BACKEND_BASE_URL}/api/municipalities/unique_municipalities/`)
      .then((res) => {
        setLiveCitiesCount(res?.data.total_unique_count);
      })
      .catch((err) => {
        console.error("Error fetching live cities count:", err);
      });
  }, []);

  useEffect(() => {
    fetchTableData();
  }, [currentPage]);

  const fetchTableData = async () => {
    setTableLoading(true);
    try {
      const response = await axios.get(`${BACKEND_BASE_URL}/api/data/`, {
        params: { page: currentPage, page_size: itemsPerPage },
      });
      setData(response.data.results);
      setTotalCount(response.data.count);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to fetch data.");
    } finally {
      setTableLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleFileUpload = async () => {
    if (!file) {
      toast.error("Please select a file before uploading.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    setLoading(true);

    try {
      await axios.post(`${BACKEND_BASE_URL}/api/upload/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setFile(null);
      toast.success("File uploaded successfully.");
      setIsFileDialogOpen(false);
      fetchTableData();
    } catch (error) {
      console.error("Error uploading file:", error);
      setError("File upload failed.");
    } finally {
      setLoading(false);
    }
  };

  const toTitleCase = (str) =>
    str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const handleAddEntry = async () => {
    try {
      const form = new FormData();

      // format text fields
      form.append("country", formData?.country__name);
      form.append("state", formData?.state);
      form.append("county", toTitleCase(formData?.county));
      form.append("municipality_name", toTitleCase(formData?.municipality_name));
      form.append("population", formData?.population || "");
      form.append("latitude", formData?.latitude || "");
      form.append("longitude", formData?.longitude || "");

      // agreement fields (only append if unlock_city is true)
      if (formData.unlock_city) {
        form.append("unlock_city", true);
        form.append("explain_requests_per_user", formData.explain_requests_per_user || "");
        form.append("ask_aware_requests_per_user", formData.ask_aware_requests_per_user || "");
        form.append("agreement_start_date", formData.agreement_start_date || "");
        form.append("agreement_end_date", formData.agreement_end_date || "");
        form.append("reminder_days", formData.reminder_days || "");
        form.append("notes", formData.notes || "");
        if (formData.attachment) {
          form.append("attachment", formData.attachment);
        }
      } else {
        form.append("unlock_city", false);
        // 🔑 forcefully reset all related fields
        form.append("explain_requests_per_user", "");
        form.append("ask_aware_requests_per_user", "");
        form.append("agreement_start_date", "");
        form.append("agreement_end_date", "");
        form.append("reminder_days", "");
        form.append("notes", "");
        form.append("attachment", "");
      }


      await axios.post(`${BACKEND_BASE_URL}/api/data/`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      fetchTableData();

      // reset form
      setFormData({
        country__name: "",
        country: "",
        state: "",
        county: "",
        municipality_name: "",
        population: "",
        latitude: "",
        longitude: "",
        unlock_city: false,
        explain_requests_per_user: 0,
        ask_aware_requests_per_user: 0,
        agreement_start_date: "",
        agreement_end_date: "",
        reminder_days: 90,
        notes: "",
        attachment: null,
      });

      setSuccessMessage("Entry added successfully.");
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error adding entry:", error);
      setError("Failed to add entry.");
    }
  };

  const handleEditEntry = async () => {
    try {
      const form = new FormData();

      // format text fields
      form.append("country", formData?.country__name || ""); // new field
      form.append("state", formData?.state);
      form.append("county", toTitleCase(formData?.county));
      form.append("municipality_name", toTitleCase(formData?.municipality_name));
      form.append("population", formData?.population || "");
      form.append("latitude", formData?.latitude || "");
      form.append("longitude", formData?.longitude || "");

      // agreement fields (only append if unlock_city is true)
      if (formData.unlock_city) {
        form.append("unlock_city", true);
        form.append("explain_requests_per_user", formData.explain_requests_per_user || "");
        form.append("ask_aware_requests_per_user", formData.ask_aware_requests_per_user || "");
        form.append("agreement_start_date", formData.agreement_start_date || "");
        form.append("agreement_end_date", formData.agreement_end_date || "");
        form.append("reminder_days", formData.reminder_days || "");
        form.append("notes", formData.notes || "");
        if (formData.attachment) {
          form.append("attachment", formData.attachment);
        }
      } else {
        form.append("unlock_city", false);
        // 🔑 forcefully reset all related fields
        form.append("explain_requests_per_user", 0);
        form.append("ask_aware_requests_per_user", 0);
        form.append("agreement_start_date", "");
        form.append("agreement_end_date", "");
        form.append("reminder_days", 90);
        form.append("notes", "");
        form.append("attachment", "");
      }


      const response = await axios.put(
        `${BACKEND_BASE_URL}/api/municipalities/${editingId}/`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const updatedData = data.map((item) =>
        item?.id === editingId ? response.data : item
      );
      setData(updatedData);

      setFormData({
        country__name: "",
        country: "",
        state: "",
        county: "",
        municipality_name: "",
        population: "",
        latitude: "",
        longitude: "",
        unlock_city: false,
        explain_requests_per_user: 0,
        ask_aware_requests_per_user: 0,
        agreement_start_date: "",
        agreement_end_date: "",
        reminder_days: 90,
        notes: "",
        attachment: null,
      });

      setSuccessMessage("Entry updated successfully.");
      setIsDialogOpen(false);
      setIsEditing(false);
      setEditingId(null);
    } catch (error) {
      console.error("Error updating entry:", error);
      setError("Failed to update entry.");
    }
  };

  const handleDeleteEntry = async (id) => {
    try {
      await axios.delete(`${BACKEND_BASE_URL}/api/municipalities/${id}/`);
      setData(data.filter((item) => item?.id !== id));
      setSuccessMessage("Entry deleted successfully.");
    } catch (error) {
      console.error("Error deleting entry:", error);
      setError("Failed to delete entry.");
    }
  };

  const handleEditDialogOpen = (entry) => {
    setIsEditing(true);
    setEditingId(entry.id);
    setFormData({
      country__name: entry?.country__name || "", // new field
      country: entry?.country || "", // Default to USA if not provided
      state: entry?.state,
      county: entry?.county,
      municipality_name: entry?.municipality_name,
      population: entry?.population,
      latitude: entry?.latitude,
      longitude: entry?.longitude,
      unlock_city: entry?.unlock_city || false,
      explain_requests_per_user: entry?.explain_requests_per_user || 0,
      ask_aware_requests_per_user: entry?.ask_aware_requests_per_user || 0,
      agreement_start_date: entry?.agreement_start_date || "",
      agreement_end_date: entry?.agreement_end_date || "",
      reminder_days: entry?.reminder_days || 90,
      notes: entry?.notes || "",
      attachment: entry.attachment || null, // user must re-upload if needed
      is_locked_from_partnerCity: entry?.is_locked_from_partnerCity || false, // new field
    });
    setIsDialogOpen(true);
  };
  const handleExport = (type) => {
    const url = `${BACKEND_BASE_URL}/api/municipalities/export/${type}/`;

    // Create a temporary link
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `municipalities.${type === "excel" ? "xlsx" : "csv"}`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value.toLowerCase());
  };

  const handleSearch = async () => {
    setSearchLoading(true);
    try {
      const response = await axios.get(`${BACKEND_BASE_URL}/api/data/`, {
        params: {
          page: 1,
          page_size: itemsPerPage,
          search: searchQuery,
        },
      });

      setData(response.data.results);
      setTotalCount(response.data.count);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error searching:", error);
    } finally {
      setSearchLoading(false);
    }
  };

  // Clear error message
  const handleCloseError = () => {
    setError(null);
  };

  // Clear success message
  const handleCloseSuccess = () => {
    setSuccessMessage(null);
  };


  // ✅ NEW: Function to handle the toggle state update
  // const handleToggle = (municipalityId, newStatus) => {
  //   setData((prevData) =>
  //     prevData.map((entry) =>
  //       entry.id === municipalityId ? { ...entry, capture_enabled: newStatus } : entry
  //     )
  //   );
  // };

  const handleToggle = (municipalityId, newStatus, captureId = null) => {
    console.log("UPDATING TABLE ROW:", { municipalityId, newStatus, captureId });

    setData(prev =>
      prev.map(entry =>
        entry.id === municipalityId
          ? {
            ...entry,
            capture_enabled: newStatus,
            capture_id: captureId
          }
          : entry
      )
    );
  };




  return (
    <>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" flexDirection="column">
            <Box display="flex" justifyContent="space-between">
              <Typography style={{ fontWeight: "bold" }} variant="h4">
                Manage Town
              </Typography>

              <Box display="flex">
                <TextField
                  placeholder="Search..."
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                  style={{ marginRight: "10px" }}
                  InputProps={{
                    startAdornment: (
                      <FontAwesomeIcon
                        icon={faMagnifyingGlass}
                        style={{ marginRight: "8px", color: "#555" }}
                      />
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  style={{ marginRight: "10px", backgroundColor: "#0B194A", color: "white" }}
                  disabled={searchLoading}
                >
                  {searchLoading ? <CircularProgress size={20} style={{ color: "white" }} /> : "Search"}
                </Button>
                {/* ✅ Export Button Added Here */}
                <Button
                  variant="contained"
                  onClick={() => handleExport("excel")}
                  style={{ backgroundColor: "#0B194A", color: "white", marginRight: "10px" }}
                >
                  Export
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setIsFileDialogOpen(true)}
                  style={{ backgroundColor: "#0B194A", color: "white", marginRight: "10px" }}
                >
                  Upload File
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setIsDialogOpen(true)}
                  style={{ backgroundColor: "#0B194A", color: "white" }}
                >
                  Add
                </Button>
              </Box>
            </Box>

            <Typography variant="subtitle1" style={{ marginTop: "5px", fontWeight: "bold" }}>
              Total Live Cities: {liveCitiesCount?.toLocaleString()}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          {tableLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <CircularProgress />
            </Box>
          ) : (
            <StateTable
              data={data}
              onEdit={handleEditDialogOpen}
              onDelete={handleDeleteEntry}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onToggle={handleToggle} // ✅ Make sure this prop is passed here
            />
          )}
        </Grid>

        <Box display="flex" justifyContent="center" mb={2} width="100%">
          <Pagination
            count={Math.ceil(totalCount / itemsPerPage)}
            page={currentPage}
            onChange={(e, value) => setCurrentPage(value)}
            color="primary"
          />
        </Box>
      </Grid>

      {/* Add/Edit Entry Dialog Component */}
      <EntryFormDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        formData={formData}
        setFormData={setFormData}
        isEditing={isEditing}
        onSubmit={isEditing ? handleEditEntry : handleAddEntry}
      />

      {/* File Upload Dialog Component */}
      <FileUploadDialog
        isOpen={isFileDialogOpen}
        onClose={() => setIsFileDialogOpen(false)}
        handleFileChange={handleFileChange}
        handleFileUpload={handleFileUpload}
        loading={loading}
      />

      {/* Error Message Snackbar */}
      {error && (
        <Snackbar
          open={Boolean(error)}
          autoHideDuration={6000}
          onClose={handleCloseError}
          message={error}
        />
      )}

      {/* Success Message Snackbar */}
      {successMessage && (
        <Snackbar
          open={Boolean(successMessage)}
          autoHideDuration={6000}
          onClose={handleCloseSuccess}
          message={successMessage}
        />
      )}
    </>
  );
}